import React, { FC, useEffect, useRef, useState } from "react";
import SearchBar from "./SearchBar";
import MobileSearch from "./MobileSearch";
import { useData } from "data/data-provider";
import RenterInfo from "./RenterInfo";
import DriverInfo from "./DriverInfo";
import toast from "react-hot-toast";
import VehicleSwap from "./VehicleSwap";
import { AddCustomer, getCustomer } from "api/customer/customer";
import InsuranceandExtras from "./InsuranceandExtras";
import { getBonzahProducts } from "api/bonzah/bonzah";
import { globalJson } from "global/global_json";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { tripModificationRequest } from "api/booking/booking";
import { formatDateAndTimeToISO} from "utils/date-format";

interface EditBookingProps {
  bookingDetails: any;
  startTime: any;
  endTime: any;
  setStartTime: any;
  setEndTime: any;
}

const EditBooking: FC<EditBookingProps> = ({
  bookingDetails,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
}) => {
  const [selectedStep, setSelectedStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isStartDateLocked, setIsStartDateLocked] = useState(false);
  const {
    setBonzahProducts,
    selectedCar,
    startDate,
    endDate,
    pickupLocation,
    dropOffLocation,
    searchedDropoffLocation,
    searchedPickupLoction,
    dropoffLocationType,
    sameDropoffLocation,
    selectedExtras,
    selectedBonzahProducts,
    driverEmail,
    driverFirstName,
    driverLastName,
    driverPhone,
    ourCompany,
  } = useData();

  const stepOptions = [
    "1. Trip Info",
    "2. Vehicle Swap",
    "3. Insurance & Extras",
    "4. Secondary Driver",
  ];

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const response = await getCustomer(bookingDetails?.customer.email);
        if (response) {
          localStorage.setItem("access_token", response.access_token);
          localStorage.setItem("refresh_token", response.refresh_token);
          localStorage.setItem("user", JSON.stringify(response));
        }
      } catch (err) {
        toast.error("An error occurred while processing your request.");
      }
    };
    fetchCustomer();
  }, []);

  const fetchBonzahProducts = async (selectedType?: string) => {
    setLoading(true);
    const payload = {
      company_id:
        process.env.REACT_APP_ENV !== "prod"
          ? globalJson.dev_company_id
          : globalJson.prod_company_id,
      selected_type: selectedType,
      fleet: selectedCar?.id,
      pickup_location: pickupLocation?.id,
    };

    const { data, error } = await getBonzahProducts(payload);

    if (data) {
      setBonzahProducts(data);
      setLoading(false);
    } else if (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBonzahProducts();
  }, [selectedCar,pickupLocation]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!driverFirstName) newErrors.driverFirstName = "First name is required";
    if (!driverLastName) newErrors.driverLastName = "Last name is required";
    if (!driverEmail) {
      newErrors.driverEmail = "Email is required";
    } else if (
      !/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(driverEmail)
    ) {
      newErrors.driverEmail = "Please enter a valid email address";
    }
    if (!driverPhone) {
      newErrors.driverPhone = "Phone number is required";
    } else if (driverPhone.length < 10) {
      newErrors.driverPhone = "Please enter a valid phone number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep = () => {
    if (!startDate || !endDate) {
      toast.error("Please select pick-up and drop-off dates");
      return false;
    }

    if (!pickupLocation) {
      toast.error("Please select a pickup location");
      return false;
    }

    if (sameDropoffLocation === false && !dropOffLocation) {
      toast.error("Please select a drop-off location");
      return false;
    }

    if (startDate && endDate && startDate.toDateString() === endDate.toDateString()) {
      if (startTime && endTime) {
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour, endMinute] = endTime.split(':').map(Number);
        const startTotalMinutes = startHour * 60 + startMinute;
        const endTotalMinutes = endHour * 60 + endMinute;
        
        if (startTotalMinutes >= endTotalMinutes) {
          toast.error("Drop-off time must be later than pick-up time");
          return false;
        }
      }
    }

    return true;
  };

  const TripModificationRequest = async () => {
    setLoading(true);
    if (driverFirstName || driverLastName || driverEmail || driverPhone) {
      if (!validateForm()) {
        setLoading(false);
        return;
      }

      if (bookingDetails?.customer?.email === driverEmail) {
        toast.error("Customer and secondary driver email cannot be the same");
        setLoading(false);
        return;
      }
      const payload = {
        company:
          process.env.REACT_APP_ENV !== "prod"
            ? globalJson.dev_company_id
            : globalJson.prod_company_id,
        first_name: driverFirstName,
        last_name: driverLastName,
        email: driverEmail,
        phone_no: driverPhone.replace(/\D/g, ""),
      };
      try {
        const response = await AddCustomer(payload);
        if (response.error) {
          toast.error(response.error);
          setLoading(false);
          return;
        }

        if (response.data) {
          const payload = {
            booking: bookingDetails?.id,
            secondary_driver: response.data.id,
            extras: Array.from(selectedExtras),
            bonzah_insurance_options: Array.from(selectedBonzahProducts),
            fleet: selectedCar?.id,
            pick_up_time: startDate
              ? formatDateAndTimeToISO(startDate, startTime)
              : "",
            drop_off_time: endDate
              ? formatDateAndTimeToISO(endDate, endTime)
              : "",
            pick_up_location: pickupLocation?.id,
            drop_off_location:
              dropoffLocationType === true
                ? pickupLocation?.id
                : dropOffLocation.id,
            custom_pick_up_address: searchedPickupLoction,
            custom_drop_off_address:
              sameDropoffLocation === false
                ? searchedDropoffLocation
                : searchedPickupLoction,
          };
          try {
            const result = await tripModificationRequest(payload);
            if (!result.error) {
              toast.success("Secondary Driver Added successfully");
              setLoading(false);
            } else {
              toast.error(result.error);
              setLoading(false);
            }
          } catch (err) {
            toast.error("An error occurred while processing your request.");
            setLoading(false);
          }
        }
      } catch {
        toast.error("An error occurred while processing your request.");
        setLoading(false);
      }
    } else {
      setErrors({});
      const payload = {
        booking: bookingDetails?.id,
        extras: Array.from(selectedExtras),
        bonzah_insurance_options: Array.from(selectedBonzahProducts),
        fleet: selectedCar?.id,
        pick_up_time: startDate
          ? formatDateAndTimeToISO(startDate, startTime)
          : "",
        drop_off_time: endDate ? formatDateAndTimeToISO(endDate, endTime) : "",
        pick_up_location: pickupLocation?.id,
        drop_off_location:
          dropoffLocationType === true
            ? pickupLocation?.id
            : dropOffLocation.id,
        custom_pick_up_address: searchedPickupLoction,
        custom_drop_off_address:
          sameDropoffLocation === false
            ? searchedDropoffLocation
            : searchedPickupLoction,
      };
    
      try {
        const result = await tripModificationRequest(payload);
        if (!result.error) {
          toast.success("Your booking modification request has been sent.");
          setLoading(false);
        } else {
          toast.error(result.error);
          setLoading(false);
        }
      } catch (err) {
        toast.error("An error occurred while processing your request.");
        setLoading(false);
      }
    }
  };

  const handleNextStep = () => {
    if (selectedStep === 0) {
      if (!validateStep()) {
        return;
      }
    }
    
    setSelectedStep((prev) => Math.min(prev + 1, stepOptions.length - 1));
    
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  const handlePreviousStep = () => {
    setSelectedStep((prev) => Math.max(prev - 1, 0));
    
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  return (
    <>
      <div ref={containerRef} className="border  my-4 p-3 md:p-6">
        <div className="mb-12 px-2 md:px-6 relative">
          <div className="absolute top-[24px] left-[50px] right-[55px] md:left-[70px] md:right-[60px] h-[2px] bg-gray-300" />
          <div className="absolute top-[24px] h-[2px] bg-[#4CAF50] transition-all duration-300" />

          <div className="flex justify-between relative">
            {stepOptions.map((option, idx) => {
              const stepName = option.split(". ")[1];
              const isActive = selectedStep >= idx;

              return (
                <div key={idx} className="flex flex-col items-center">
                  <div
             
                    className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-2 md:mb-3
                    ${
                      isActive
                        ? "bg-primary-100 text-white"
                        : "bg-white border-2 border-gray-300 text-gray-500"
                    }`}
                  >
                    {idx + 1}
                  </div>
                  <span
                    className={`text-xs md:text-sm text-center max-w-[80px] md:max-w-none ${
                      isActive ? "text-primary-100" : "text-gray-500"
                    }`}
                  >
                    {stepName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {selectedStep === 0 && (
          <>
            <div className="hidden lg:flex">
              <SearchBar
                startTime={startTime}
                setStartTime={setStartTime}
                endTime={endTime}
                setEndTime={setEndTime}
                bookingDetails={bookingDetails}
                isStartDateLocked={isStartDateLocked}
                setIsStartDateLocked={setIsStartDateLocked}
              />
            </div>
            <div className="flex lg:hidden  mt-4">
              <MobileSearch
                startTime={startTime}
                setStartTime={setStartTime}
                endTime={endTime}
                setEndTime={setEndTime}
                bookingDetails={bookingDetails}
                isStartDateLocked={isStartDateLocked}
                setIsStartDateLocked={setIsStartDateLocked}
              />
            </div>
          </>
        )}
        {selectedStep === 1 && (
          <>
            <VehicleSwap
              bookingDetails={bookingDetails}
              setSelected={setSelectedStep}
            />
          </>
        )}
        {selectedStep === 2 && (
          <>
            <InsuranceandExtras bookingDetails={bookingDetails} />
          </>
        )}
        {selectedStep === 3 && (
          <>
            <DriverInfo
              bookingDetails={bookingDetails}
              setErrors={setErrors}
              errors={errors}
            />
          </>
        )}

        <div className="flex justify-between mt-4">
          <ButtonPrimary
            disabled={selectedStep === 0}
            onClick={handlePreviousStep}
            className="disabled:opacity-50 rounded-md bg-white !text-black border !border-black min-w-32"
          >
            Previous
          </ButtonPrimary>
          {selectedStep < stepOptions.length - 1 ? (
            <ButtonPrimary
              onClick={handleNextStep}
              className="disabled:opacity-50 rounded-md min-w-32"
            >
              Next
            </ButtonPrimary>
          ) : (
            <ButtonPrimary
              onClick={TripModificationRequest}
              className="disabled:opacity-50 rounded-md min-w-32"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update"}
            </ButtonPrimary>
          )}
        </div>
      </div>
    </>
  );
};

export default EditBooking;
